import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DataServiceError } from '@ngrx/data';
import { PlAnimations } from '@common/animations/PlAnimations';
import { nameErrorHandler } from '@common/constant';
import { PlClientService } from '@common/entities';
import { Client } from '@common/models';
import { LocationService } from '@common/services/location.service';
import { PL_GRADE_OPTIONS } from '@app/client/constants';
import { DateTime } from 'luxon';
import { map, Observable } from 'rxjs';
import { PlOption, plNameValidators } from 'pl-core-services';

const CLIENT_EXISTS_ERROR = 'client_exists_error';

@Component({
  selector: 'pl-client-quick-add',
  templateUrl: './client-quick-add.component.html',
  styleUrls: ['./client-quick-add.component.scss'],
  animations: [...PlAnimations.fade],
})
export class ClientQuickAddComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('firstNameInput') firstNameInput: ElementRef<HTMLInputElement>;

  @Input() client?: Client;

  @Output() back = new EventEmitter<void>();

  @Output() saved = new EventEmitter<Client>();

  saving = false;

  readonly grades = PL_GRADE_OPTIONS;

  readonly firstName = new FormControl('', {
    nonNullable: true,
    validators: plNameValidators,
  });

  readonly lastName = new FormControl('', {
    nonNullable: true,
    validators: plNameValidators,
  });

  readonly dateOfBirth = new FormControl<DateTime | null>(null, {
    validators: [Validators.required],
  });

  readonly grade = new FormControl<string | null>(null);

  readonly accountLocation = new FormControl<string | null>(null);

  readonly studentId = new FormControl<string | null>(null);

  readonly formGroup = new FormGroup({
    first_name: this.firstName,
    last_name: this.lastName,
    date_of_birth: this.dateOfBirth,
    grade_display: this.grade,
    account_location: this.accountLocation,
    external_id: this.studentId,
  });

  nameError: Function = nameErrorHandler;

  locationOptions$: Observable<PlOption[]>;

  constructor(
    private locationService: LocationService,
    private clientService: PlClientService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.locationOptions$ = this.locationService.getLocations().pipe(
      map((locations) =>
        locations.map((location) => ({
          value: location.location_uuid,
          label: location.name,
        }))
      )
    );

    if (this.client) {
      this.formGroup.patchValue(this.client);
    }
  }

  ngAfterViewInit(): void {
    // Focusing synchronously here would change mat-form-field's focus-state
    // host bindings in the middle of the change-detection pass that just
    // checked this view, triggering ExpressionChangedAfterItHasBeenChecked.
    // Deferring to a microtask lets that pass finish first.
    Promise.resolve().then(() => this.firstNameInput.nativeElement.focus());
  }

  get isEditMode(): boolean {
    return !!this.client?.uuid;
  }

  get maxDateOfBirth(): DateTime {
    return DateTime.now();
  }

  get dateOfBirthError(): string {
    if (this.dateOfBirth.hasError('matDatepickerParse')) {
      return 'Use the format MM/DD/YYYY';
    }
    return 'Please enter a valid date';
  }

  save(): void {
    if (this.formGroup.invalid || this.saving) {
      return;
    }

    this.saving = true;
    const wasEditMode = this.isEditMode;

    const payload: Client = this.isEditMode
      ? { ...this.client, ...this.formGroup.getRawValue() }
      : this.formGroup.getRawValue();

    const request$ = this.isEditMode
      ? this.clientService.update(payload)
      : this.clientService.add(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (savedClient) => {
        this.saving = false;
        this.showSuccessBanner(savedClient, wasEditMode);
        this.saved.emit(savedClient);
      },
      error: (error) => {
        this.saving = false;
        this.showErrorBanner(error);
      },
    });
  }

  private showSuccessBanner(client: Client, wasEditMode: boolean): void {
    const firstName = client.first_name ?? this.firstName.value;
    const lastName = client.last_name ?? this.lastName.value;
    const name = `${firstName} ${lastName}`;
    const message = wasEditMode
      ? `${name} has been updated.`
      : `${name} has been added to your caseload.`;
    this.snackBar.open(message, 'X', {
      duration: 7000,
      verticalPosition: 'top',
      panelClass: ['pl-snackbar-success'],
    });
  }

  private showErrorBanner(error: unknown): void {
    const message = this.isClientExistsError(error)
      ? 'A student with this name and date of birth already exists.'
      : 'Something went wrong saving this student. Please try again.';
    this.snackBar.open(message, 'X', {
      duration: 10000,
      verticalPosition: 'top',
      panelClass: ['pl-snackbar-error'],
    });
  }

  // ngrx/data wraps HTTP failures from PlClientService.add()/update() in a
  // DataServiceError, whose `.error` is the raw HttpErrorResponse, whose own
  // `.error` is the parsed API response body.
  private isClientExistsError(error: unknown): boolean {
    const httpError = error instanceof DataServiceError ? error.error : error;
    const body =
      httpError instanceof HttpErrorResponse ? httpError.error : undefined;
    return body?.error === CLIENT_EXISTS_ERROR;
  }
}
